import React, { useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useGetChoices } from "@features/ix/hooks";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { BUTTON_FIELD_CLASSES } from "@shared/utils/constants";
import {
  useCreateConnectionFlow,
  useGetAvailableFlows,
} from "@features/ix/erpIntegration/hooks";

interface CreateSyncFlowDialogueProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (newFlow: {
    objectType: string;
    directionLabel: string;
    syncType: string;
    scheduleLabel: string;
    operationKey: string;
  }) => void;
}

interface SyncFlowFormData {
  objectType: string;
  directionLabel: string;
  syncType: string;
  scheduleLabel: string;
}

export const CreateSyncFlowDialogue: React.FC<CreateSyncFlowDialogueProps> = ({
  isOpen,
  onClose,
  onAdd,
}) => {
  const { id } = useParams<{ id: string }>();
  const { data: availableFlowsData } = useGetAvailableFlows(id);
  const { mutateAsync: createFlow } = useCreateConnectionFlow(id);
  const { data: directionData } = useGetChoices("direction");
  const { data: scheduleData } = useGetChoices("schedule_type");

  const directionChoices = useMemo(
    () =>
      directionData?.map((d: any) => ({
        value: d.key,
        label: d.value,
      })) || [],
    [directionData],
  );

  const scheduleChoices = useMemo(
    () =>
      scheduleData?.map((d: any) => ({
        value: d.key,
        label: d.value,
      })) || [],
    [scheduleData],
  );

  const objectTypeOptions = useMemo(() => {
    return availableFlowsData?.map((d: any) => ({
      value: d.objectType,
      label: d.label,
    }));
  }, [availableFlowsData]);

  // 1. Define fields for the Add flow form using standard select types
  const fields: FormField[] = useMemo(
    () => [
      {
        name: "directionLabel",
        label: "Direction",
        type: "select",
        required: true,
        placeholder: "Select direction",
        className: "col-span-1",
        options: directionChoices,
      },
      {
        name: "objectType",
        label: "Object type",
        type: "select",
        required: true,
        placeholder: "Select object type",
        className: "col-span-1",
        options: objectTypeOptions,
      },
      {
        name: "syncType",
        label: "Sync type",
        type: "select",
        required: false,
        placeholder: "Select sync type",
        className: "col-span-2",
        disabled: (values: any) => Number(values?.directionLabel) === 1,
        helperText:
          "Sync type cannot be set for Inbound — data is only fetched from SMART360.",
        options: [
          { value: "realtime", label: "Real-Time" },
          { value: "scheduled", label: "Scheduled" },
          { value: "manual", label: "Manual" },
        ],
      },
      {
        name: "scheduleLabel",
        label: "Schedule",
        type: "select",
        required: true,
        placeholder: "Select schedule",
        showWhen: (values: any) =>
          values?.syncType === "scheduled" &&
          Number(values?.directionLabel) !== 1,
        className: "col-span-2",
        options: scheduleChoices,
      },
    ],
    [objectTypeOptions, directionChoices, scheduleChoices],
  );

  // 2. Initialize form state
  const form = FormService.createForm<SyncFlowFormData>({
    fields,
    mode: "onChange",
    defaultValues: {
      objectType: "",
      directionLabel: "",
      syncType: "",
      scheduleLabel: "",
    },
  });

  const handleFieldChange = useCallback(
    (name: string, val: any) => {
      if (name === "directionLabel") {
        if (Number(val) === 1) {
          form.setValue("syncType", "");
          form.setValue("scheduleLabel", "");
        }
      }
    },
    [form],
  );

  const handleReset = useCallback(() => {
    form.reset({
      objectType: "",
      directionLabel: "",
      syncType: "",
      scheduleLabel: "",
    });
  }, [form]);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  const onSubmit = (data: SyncFlowFormData) => {
    const payload: any = {
      objectType: data.objectType,
      direction: Number(data.directionLabel),
    };

    if (Number(data.directionLabel) !== 1) {
      if (data.syncType) {
        payload.syncType = data.syncType;
      }
      if (data.syncType === "scheduled" && data.scheduleLabel) {
        payload.scheduleLabel = String(data.scheduleLabel);
      }
    }

    createFlow(payload);
    handleClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add a new sync flow</DialogTitle>
          <DialogDescription>
            Configure the sync flow object, direction, and schedule settings
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <DynamicForm
            fields={fields}
            form={form}
            onSubmit={onSubmit}
            onReset={handleClose}
            onFieldChange={handleFieldChange}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: "Add Flow",
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: BUTTON_FIELD_CLASSES,
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSyncFlowDialogue;
